<template>
	<div>
		<CardGrafico style="width: 80vw; padding: 0">
			<template #grafico>
				<a
					class="hover:underline text-sm"
					@click="exportarOutliersMedicao"
					>{{ $t('exportarAnaliseMedicao') }}
				</a>
				<div class="container__grafico__dispersao">
					<slot name="action-buttons" />
					<div class="flex justify-between gap-40">
						<div class="flex gap-10">
							<Dropdown
								v-model="variavelEixoX"
								:options="opcoesEixoXComputada"
								:label="`${$t('variable')} ${$t('xAxis')}`"
							>
								<template #tooltip>
									<div class="w-fit block">
										<Tooltip :options="{ placement: 'top' }">
											<template #trigger>
												<Icon
													icon="octicon:question-16"
													class="text-gray-medium"
													style="width: 13px"
												/>
											</template>
											<div class="flex flex-col gap-2 max-w-xs p-2">
												<p class="text-s-bold">Variável Eixo X</p>
												<p class="text-s">Selecione qual variável deseja exibir no eixo X.</p>
											</div>
										</Tooltip>
									</div>
								</template>
							</Dropdown>
							<Dropdown
								v-model="variavelEixoY"
								:options="opcoesEixoYComputada"
								:label="`${$t('variable')} ${$t('yAxis')}`"
							>
								<template #tooltip>
									<div class="w-fit block">
										<Tooltip :options="{ placement: 'top' }">
											<template #trigger>
												<Icon
													icon="octicon:question-16"
													class="text-gray-medium"
													style="width: 13px"
												/>
											</template>
											<div class="flex flex-col gap-2 max-w-xs p-2">
												<p class="text-s-bold">Variável Eixo Y</p>
												<p class="text-s">
													Selecione qual variável deseja exibir no eixo Y. Apenas variáveis
													quantitativas podem ser selecionadas para permitir a comparação com as
													variáveis qualitativas do eixo X.
												</p>
											</div>
										</Tooltip>
									</div>
								</template>
							</Dropdown>
						</div>
						<div class="flex gap-10">
							<MultiSelect
								v-model="talhoesEscolhidos"
								:options="talhoesOptions"
								:label="$t('section')"
							/>
							<MultiSelect
								v-model="parcelasEscolhidas"
								:options="parcelasOptions"
								:label="$t('plot')"
							/>
						</div>
					</div>

					<div style="display: flex">
						<div
							class="legenda"
							style="min-width: max-content; height: fit-content; border-radius: 5px; padding: 1rem"
						>
							<div class="py-4 flex flex-col gap-6">
								<div>
									<label class="text-label">visualização de categoria</label>
									<Checkbox
										v-model="showCategoria"
										:disabled="medicoesAgrupadasPorCategoriaFitossanidade['null']?.length > 0"
										label="Mostrar categoria de fitossanidade"
									/>
								</div>

								<div>
									<div class="flex gap-2">
										<label class="text-label">exibir apenas dados das flags</label>
										<div class="w-fit block">
											<Tooltip :options="{ placement: 'top' }">
												<template #trigger>
													<Icon
														icon="octicon:question-16"
														class="text-gray-medium"
														style="width: 13px"
													/>
												</template>
												<div class="flex flex-col gap-2 max-w-xs p-2">
													<p class="text-s-bold">Exibir apenas dados das flags</p>
													<p class="text-s">
														As flags são o formato de alerta do sistema para possíveis erros ou
														inconsistências no momento da coleta. O sistema SmartForest possui dois
														tipos de flags:
													</p>
													<p class="text-s-bold">Flags comparativas</p>
													<p class="text-s">
														Apresentam alertas que comparam os dados que estão sendo inseridos para
														verificar se eles fazem sentido no contexto (Ex.: O DAP não é compatível
														com a idade)
													</p>
													<p class="text-s-bold">Flags de consistência</p>
													<p class="text-s">
														Para Ordens de Serviço que possuem validação com o inventário anterior,
														as flags demonstram qual a inconsistência que foi encontrada na coleta
														(Ex.: O diâmetro anterior é maior que o diâmetro atual)
													</p>
													<p class="text-s">
														Ao habilitar a checkbox abaixo, o usuário poderá filtrar apenas os dados
														que possuem uma (ou ambas) as flags.
													</p>
												</div>
											</Tooltip>
										</div>
									</div>

									<div class="flex justify-between gap-4">
										<Checkbox
											v-model="showFlagsComparativas"
											:disabled="!temFlagComparativa"
											label="Comparativas"
										/>
										<Checkbox
											v-model="showFlagsConsistencia"
											:disabled="!temFlagConsistenciaDados"
											label="Consistência de dados"
										/>
									</div>
								</div>
							</div>
							<label>{{ !showCategoria ? $t('treeCategory') : $t('fitoCategory') }}</label>
							<div
								v-if="categoriasArvore.length != 0"
								class="categoria-legenda"
							>
								<div
									v-for="categoria in Object.keys(
										!showCategoria
											? medicoesAgrupadasPorCategoria
											: medicoesAgrupadasPorCategoriaFitossanidade,
									)"
									:key="categoria"
									:style="hideCategorias.includes(categoria) ? 'opacity: 0.5;' : 'opacity: 1;'"
									style="display: flex; gap: 1rem; cursor: pointer"
									@click="toggleCategoria(categoria)"
								>
									<div
										:style="`height: 14px; width: 14px; border-radius: 50%; background-color: ${!showCategoria ? corCategoriaArvore[categoria] : corCategoriaFitossanidade[categoria]}`"
									/>
									<p style="font-size: 0.8rem; margin-bottom: 0.3rem">
										{{ categoria }}
										({{ quantidadePorCategoria(categoria) }})
									</p>
								</div>
							</div>
							<label>Níveis de Risco</label>
							<div class="categoria-risco">
								<div
									v-for="simbolo in Object.keys(medicoesAgrupadasPorNivelRisco)"
									:key="simbolo"
									:style="hideRisco.includes(simbolo) ? 'opacity: 0.5;' : 'opacity: 1;'"
									style="display: flex; gap: 1rem; cursor: pointer"
									@click="toggleRisco(simbolo)"
								>
									<svg
										v-if="simbolo === 'CORRETO'"
										height="14"
										width="14"
									>
										<circle
											cx="7"
											cy="7"
											r="6"
											stroke="grey"
											stroke-width="2"
											fill="transparent"
										/>
									</svg>

									<svg
										v-else-if="simbolo === 'RISCO MÉDIO'"
										width="14"
										height="14"
									>
										<rect
											width="13"
											height="13"
											stroke="grey"
											stroke-width="2"
											fill="transparent"
										/>
									</svg>

									<svg
										v-else-if="simbolo === 'RISCO ALTO'"
										width="14"
										height="14"
										preserveAspectRatio="none"
										viewBox="0 0 167 90"
										fill="none"
									>
										<path
											vector-effect="non-scaling-stroke"
											d="M2.10786 45L83.5 1.13597L164.892 45L83.5 88.864L2.10786 45Z"
											fill="transparent"
											stroke="grey"
											stroke-width="2"
										/>
									</svg>

									<svg
										v-else-if="simbolo === 'RISCO BAIXO'"
										height="14"
										width="14"
									>
										<polygon
											points="0,0 7,14 14,0"
											stroke="grey"
											stroke-width="2"
											fill="transparent"
										/>
									</svg>

									<svg
										v-else
										height="14"
										width="14"
									>
										<polygon
											points="7,0 14,14 0,14"
											stroke="grey"
											stroke-width="2"
											fill="transparent"
										/>
									</svg>

									<p style="font-size: 0.8rem; margin-bottom: 0.3rem">
										{{ simbolo }}
										({{
											medicoesAgrupadasPorNivelRisco[simbolo].filter(
												(f) => !medicoesDesconsideradas.includes(f.id),
											).length
										}})
									</p>
								</div>
							</div>
						</div>
						<div class="container_grid__outliers">
							<div class="container_grid__outliers_boxplot_dap">
								<Chart
									id="dipersao__arvores__com__dap"
									:key="`dap-${variavelEixoX}-${showCategoria}-${hideCategorias.join(',')}`"
									ref="ref__chart__dap"
									style="width: 100%; min-height: 20vh; height: 100%"
									:options="chartOptionsDAP"
								/>
							</div>
							<div class="container_grid__outliers_boxplot_space" />
							<div class="container_grid__outliers_boxplot_dap_ht">
								<Chart
									id="dispersao__parcelas"
									:key="`main-${variavelEixoX}-${showCategoria}-${hideCategorias.join(',')}`"
									ref="ref__chart"
									style="width: 100%; min-height: 50vh; height: 100%"
									:options="chartOptions"
								/>
							</div>
							<div class="container_grid__outliers_boxplot_ht">
								<Chart
									id="dipersao__arvores__com__ht"
									:key="`ht-${variavelEixoX}-${showCategoria}-${hideCategorias.join(',')}`"
									ref="ref__chart__ht"
									style="width: 100%; min-height: 20vh; height: 100%"
									:options="chartOptionsHT"
								/>
							</div>
						</div>
					</div>
				</div>
			</template>
		</CardGrafico>
		<CrudForm
			:is-visible="mostrarCrudForm"
			:change-is-visible="
				() => {
					mostrarCrudForm = false
					pointToEdit = undefined
				}
			"
			:title-form="`${$t('edition')} ${$t('measurement')}`"
			:backdrop="true"
		>
			<template
				v-if="pointToEdit"
				#form
			>
				<EditMedicaoForm
					:viewing="viewing"
					:disabled="editing"
					:categorias="categorias"
					:categorias-fito="categoriasFitossociologia"
					:medicao="pointToEdit"
					@delete="desconsiderarMedicao"
					@save="emitEditedPoint"
				/>
			</template>
		</CrudForm>
	</div>
</template>

<script>
import { Icon } from '@iconify/vue2'
import download from 'downloadjs'
import { Chart } from 'highcharts-vue'
import OutliersMedicaoMixins from '../../mixins/OutliersMedicaoMixins'
import { outliersMessage } from '../../utils/enums/OutliersMessage'
import CardGrafico from '../CardGrafico/CardGrafico'
import CrudForm from '../CrudForm/CrudForm'
import Dropdown from '../Dropdown/Dropdown'
import Checkbox from '../ui/checkbox/Checkbox.vue'
import EditMedicaoForm from './EditMedicaoForm.vue'
import { useChartTooltip } from './utils'
import { ValidacaoConsistenciaMessagesFactory } from '../../factories/ValidacaoConsistenciaMessagesFactory'

import _ from 'lodash'
import MultiSelect from '../MultiSelect/MultiSelect.vue'
import Tooltip from '../ui/tooltip/Tooltip.vue'

export default {
	name: 'OutliersChart',
	components: {
		// ButtonDropdown,
		CardGrafico,
		Chart,
		Dropdown,
		// ToggleSwitch,
		MultiSelect,
		CrudForm,
		EditMedicaoForm,
		Icon,
		Tooltip,
		Checkbox,
	},
	mixins: [OutliersMedicaoMixins],
	props: {
		opcoesEixoX: { type: Array },
		opcoesEixoY: { type: Array },
		bindColunas: { type: Object },
		selectOnZoom: { type: Boolean, default: false },
		viewing: { type: Boolean, default: false },
		series: { type: Array, required: true },
		updatedAt: { type: String, required: false, default: '' },
		options: {
			type: Array,
			required: false,
			default: () => {
				return []
			},
		},
	},
	data() {
		return {
			talhoesEscolhidos: [],
			parcelasEscolhidas: [],

			showCategoria: false,

			showFlagsComparativas: false,
			showFlagsConsistencia: false,

			variavelEixoX: 'dap',
			variavelEixoY: 'ht',

			editing: false,
			medicoesDesconsideradas: [],
			pointToEditId: undefined,
			pointToEdit: undefined,
			hideCategorias: [],
			hideRisco: [],
			redraw: true,
			eixoXMaximoComZoom: 0,
			eixoXMinimoComZoom: 0,
			eixoYMinimoComZoom: 0,
			eixoYMaximoComZoom: 0,
			mostrarCrudForm: false,
		}
	},
	computed: {
		temFlagComparativa() {
			return this.series.some((medicao) => medicao.insights.length)
		},
		temFlagConsistenciaDados() {
			return this.series.some((medicao) => medicao.validacaoConsistencia.length)
		},
		temDadosQualidade() {
			return !this.medicoesAgrupadasPorCategoriaFitossanidade['null']?.length > 0
		},
		opcoesEixoXComputada() {
			return this.opcoesEixoX.map((opcao) =>
				Object.assign(opcao, { disabled: !this.temDadosQualidade && opcao.qualidade }),
			)
		},
		opcoesEixoYComputada() {
			return this.opcoesEixoY.map((opcao) =>
				Object.assign(opcao, { disabled: !this.temDadosQualidade && opcao.qualidade }),
			)
		},
		talhoesOptions() {
			return [...new Set(this.series.map((m) => m.talhao))].map((m) => {
				return { value: m, label: m }
			})
		},
		parcelasOptions() {
			const filtered = this.talhoesEscolhidos.length
				? this.series.filter((m) => this.talhoesEscolhidos.includes(m.talhao)).map((m) => m.parcela)
				: this.series.map((m) => m.parcela)

			return [...new Set(filtered)].map((m) => {
				return { value: m, label: m }
			})
		},
		corCategoriaArvore() {
			return Object.fromEntries(
				_.uniqBy(
					this.series.map(({ categoriaDaArvore, corCategoriaArvore }) => [
						categoriaDaArvore,
						corCategoriaArvore,
					]),
					(o) => o[0],
				),
			)
		},
		corCategoriaFitossanidade() {
			return Object.fromEntries(
				_.uniqBy(
					this.series.map(({ categoriaFitossanidade, corCategoriaFitossanidade }) => [
						categoriaFitossanidade,
						corCategoriaFitossanidade,
					]),
					(o) => o[0],
				),
			)
		},
		medicoesNivelRisco() {
			return this.series.map((medicao) => {
				medicao.insights = []
				if (medicao.ht) {
					this.validaLimitesHTxIdade(medicao)
					this.validaLimitesDAPHTxIdade(medicao)
				}
				this.validaContextoLimitesContexto(medicao)
				this.setRiscoMedicao(medicao)
				return medicao
			})
		},
		categorias() {
			return this.categoriasArvore.map((m) => {
				return {
					label: m.ctga_nm_nome,
					value: m.ctga_nm_nome,
				}
			})
		},
		categoriasFitossociologia() {
			return (
				this.categoriasFitossanidade?.map((m) => {
					return {
						label: m.nome,
						value: m.nome,
					}
				}) || []
			)
		},
		medicoesAgrupadasPorNivelRisco() {
			return _.groupBy(
				this.medicoesNivelRisco.filter((f) => f.categoriaDaArvore !== ''),
				'nivelRisco',
			)
		},
		medicoesAgrupadasPorCategoria() {
			return _.groupBy(
				this.medicoesNivelRisco.filter(
					(f) => f.categoriaDaArvore != 'FALHA' && f.categoriaDaArvore !== '',
				),
				'categoriaDaArvore',
			)
		},
		medicoesAgrupadasPorCategoriaFitossanidade() {
			return _.groupBy(
				this.medicoesNivelRisco.filter(
					(f) => f.categoriaFitossanidade && f.categoriaFitossanidade !== '' && f.categoriaFitossanidade !== null,
				),
				'categoriaFitossanidade',
			)
		},
		medicoesAgrupadasQualitativo() {
			return _.groupBy(this.medicoesNivelRisco, this.variavelEixoX)
		},
		selectedPoints() {
			return this.$refs.ref__chart.chart
		},
		boxPlotDAP() {
			return this.$refs.ref__chart__dap.chart.zoomToPoints([{ x: 5, y: 0 }])
		},

		/**
		 * Options para configuracao do grafico com DAP e com HT
		 */
		chartOptions() {
			return {
				tooltip: {
					formatter: useChartTooltip,
					backgroundColor: '#FEFEFE',
					borderRadius: 10,
					borderWidth: 2,
					borderColor: '#272728',
					outside: true,
					shadow: false,
					useHTML: true,
				},
				chart: {
					type: 'scatter',
					zoomType: 'xy',
					events: {
						selection: (e) => {
							if (!e.resetSelection) {
								this.parcelasTemporarias = this.dadosSeries[0].data.filter(
									(point) =>
										point[0] >= e.xAxis[0].min &&
										point[0] <= e.xAxis[0].max &&
										point[1] >= e.yAxis[0].min &&
										point[1] <= e.yAxis[0].max,
								)
								this.$emit('selectedPoints', this.parcelasTemporarias)

								this.eixoXMinimoComZoom = e.xAxis[0].min
								this.eixoXMaximoComZoom = e.xAxis[0].max
								this.eixoYMinimoComZoom = e.yAxis[0].min
								this.eixoYMaximoComZoom = e.yAxis[0].max

								return true // Don't zoom
							} else {
								this.eixoXMinimoComZoom = 0
								this.eixoXMaximoComZoom = 0
								this.eixoYMinimoComZoom = 0
								this.eixoYMaximoComZoom = 0

								this.$emit('selectedPoints', [])
							}
						},
					},
					// redraw: true
				},
				boost: {
					useGPUTranslations: true,
					usePreAllocated: true,
				},
				title: {
					text: '',
				},

				subtitle: {
					text: '',
				},
				xAxis: {
					title: {
						enabled: true,
						text: this.opcoesEixoX.find((f) => f.value === this.variavelEixoX).label,
						style: {
							fontSize: '100%',
						},
					},
					min: this.isQualitativo ? undefined : this.eixoXMinimo,
					max: this.isQualitativo ? undefined : this.eixoXMaximo,
					tickInterval: this.isQualitativo ? undefined : Math.round(this.eixoXMaximo / 10),
					startOnTick: !this.isQualitativo,
					endOnTick: !this.isQualitativo,
					showLastLabel: true,
					gridLineWidth: 1,
					categories: this.isQualitativo ? this.categories : undefined,
				},
				yAxis: {
					title: {
						text: this.opcoesEixoY.find((f) => f.value === this.variavelEixoY).label,
						style: {
							fontSize: '100%',
						},
					},
					min: this.eixoYMinimo,
					max: this.eixoYMaximo || 10,
					tickInterval: this.eixoYMaximo ? Math.round(this.eixoYMaximo / 10) : 1,
					legend: {
						enabled: true,
					},
					gridLineWidth: 1,
				},
				legend: {
					layout: 'vertical',
					align: 'left',
					verticalAlign: 'top',
					x: 100,
					y: 70,
					floating: true,
					enabled: false,
					borderWidth: 1,
				},
				plotOptions: {
					spline: {
						marker: { enabled: false },
						states: { hover: { enabled: true } },
					},
					scatter: {
					jitter: {
						x: 0.24,
						y: 0.1,
					},
						marker: {
							radius: 5,
							states: {
								hover: {
									enabled: true,
									lineColor: 'rgb(100,100,100)',
								},
							},
						},
						states: {
							hover: {
								marker: {
									enabled: true,
								},
							},
						},
						tooltip: {
							positioner: function () {
								return { x: 40, y: 25 }
							},
						},
					},

					series: {
						allowPointSelect: true,
						point: {
							events: {
								click: (e) => {
									this.pointToEditId = e.point.id
									this.pointToEdit =
										this.medicoesNivelRisco.find((ponto) => ponto.id === e.point.id) || undefined
									this.mostrarCrudForm = true
									console.log(this.$refs.ref__chart__dap.chart)
								},
								select: () => {
									this.$emit('selectedPoints', this.$refs.ref__chart.chart.getSelectedPoints())
								},
							},
						},
					},
				},

				series: this.dadosSeries,
			}
		},

		/**
		 * Options para configuracao do grafico com arvores com DAP
		 */
		chartOptionsDAP() {
			return {
				tooltip: {
					formatter: useChartTooltip,
					backgroundColor: '#FEFEFE',
					borderRadius: 10,
					borderWidth: 2,
					borderColor: '#272728',
					outside: true,
					shadow: false,
					useHTML: true,
				},
				chart: {
					type: 'boxplot',
					inverted: true,
					zoomType: 'xy',
					height: 150,
					events: {
						selection: (e) => {
							if (!e.resetSelection) {
								this.eixoXMinimoComZoom = e.yAxis[0].min
								this.eixoXMaximoComZoom = e.yAxis[0].max

								return true
							} else {
								this.eixoXMinimoComZoom = 0
								this.eixoXMaximoComZoom = 0
							}
						},
					},
				},
				legend: {
					enabled: false,
				},
				turboThreshold: Number.MAX_SAFE_INTEGER,
				title: {
					text: '',
				},
				credits: {
					enabled: false,
				},
				exporting: {
					enabled: false,
				},
				yAxis: {
					title: {
						enabled: true,
						text: this.opcoesEixoX.find((f) => f.value === this.variavelEixoX).label,
					},
					verticalAlign: 'top',
					startOnTick: true,
					endOnTick: true,
					showLastLabel: true,
					opposite: true,
					min: this.eixoXMinimo,
					max: this.eixoXMaximo,
					tickInterval: Math.round(this.eixoXMaximo / 10),
				},
				xAxis: {
					title: {
						text: 'Teste',
						style: {
							color: 'white',
						},
					},
					labels: {
						style: {
							color: 'white',
						},
					},
					max: 0,
					min: 0,
				},
				plotOptions: {
boxplot: {
					fillColor: 'rgba(255, 0, 0, 0.2)',
					lineWidth: 2,
					color: '#FF0000',
					medianColor: '#FF0000',
					medianWidth: 2,
					stemColor: '#FF0000',
					stemDashStyle: 'solid',
					stemWidth: 2,
					whiskerColor: '#FF0000',
					whiskerLength: '50%',
					whiskerWidth: 2,
				},
									series: {
						events: {
							click: function (event) {
								console.log(event)
							},
						},
					},
					scatter: {
					jitter: {
						x: 0.24,
						y: 0.1,
					},
						marker: {
							radius: 5,
							states: {
								hover: {
									lineColor: 'rbg(100, 100, 100)',
									enabled: true,
								},
							},
						},
						states: {
							hover: {
								marker: {
									enabled: true,
								},
							},
						},
					},
				},
				series: this.dadosSeriesDAP,
			}
		},

		/**
		 * Options para configucao do grafico de arvores com HT
		 */
		chartOptionsHT() {
			return {
				tooltip: {
					formatter: useChartTooltip,
					backgroundColor: '#FEFEFE',
					borderRadius: 10,
					borderWidth: 2,
					borderColor: '#272728',
					outside: true,
					shadow: false,
					useHTML: true,
					style: {
						width: 'auto',
					},
				},
				chart: {
					type: 'boxplot',
					zoomType: 'xy',
					events: {
						selection: (e) => {
							if (!e.resetSelection) {
								this.eixoYMinimoComZoom = e.yAxis[0].min
								this.eixoYMaximoComZoom = e.yAxis[0].max

								return true
							} else {
								this.eixoYMinimoComZoom = 0
								this.eixoYMaximoComZoom = 0
							}
						},
					},
				},
				legend: {
					enabled: false,
				},
				turboThreshold: Number.MAX_SAFE_INTEGER,
				title: {
					text: '',
				},
				credits: {
					enabled: false,
				},
				exporting: {
					enabled: false,
				},
				yAxis: {
					title: {
						enabled: true,
						text: this.opcoesEixoY.find((f) => f.value === this.variavelEixoY).label,
					},
					verticalAlign: 'top',
					startOnTick: true,
					endOnTick: true,
					showLastLabel: true,
					opposite: true,
					min: this.eixoYMinimo,
					max: this.eixoYMaximo,
					tickInterval: Math.round(this.eixoYMaximo / 10),
				},
				xAxis: {
					title: {
						text: 'Teste',
						style: {
							color: 'white',
						},
					},
					labels: {
						style: {
							color: 'white',
						},
					},
					max: 0,
					min: 0,
				},
				plotOptions: {
boxplot: {
					fillColor: 'rgba(255, 0, 0, 0.2)',
					lineWidth: 2,
					color: '#FF0000',
					medianColor: '#FF0000',
					medianWidth: 2,
					stemColor: '#FF0000',
					stemDashStyle: 'solid',
					stemWidth: 2,
					whiskerColor: '#FF0000',
					whiskerLength: '50%',
					whiskerWidth: 2,
				},
									series: {
						events: {
							click: function (event) {
								console.log(event)
							},
						},
					},
					scatter: {
					jitter: {
						x: 0.24,
						y: 0.1,
					},
						marker: {
							radius: 5,
							states: {
								hover: {
									lineColor: 'rbg(100, 100, 100)',
									enabled: true,
								},
							},
						},
						states: {
							hover: {
								marker: {
									enabled: true,
								},
							},
						},
					},
				},
				series: this.dadosSeriesHT,
			}
		},
		symbol() {
			return {
				CORRETO: 'circle',
				VERIFICAR: 'triangle',
				'RISCO BAIXO': 'triangle-down',
				'RISCO MÉDIO': 'square',
				'RISCO ALTO': 'diamond',
			}
		},
		dadosSeriesQualitativo() {
			return Object.keys(this.medicoesAgrupadasQualitativo).map((key, index) => {
				return {
					name: key,
					data: this.medicoesAgrupadasQualitativo[key]
						.filter((medicao) => !this.medicoesDesconsideradas.includes(medicao.id))
						.filter((medicao) => {
							// Filtra baseado na categoria que está sendo mostrada
							const categoria = !this.showCategoria
								? medicao.categoriaDaArvore
								: medicao.categoriaFitossanidade
							return !this.hideCategorias.includes(categoria)
						})
						.filter((medicao) => !this.hideRisco.includes(medicao.nivelRisco))
						.filter((medicao) =>
							this.showFlagsComparativas && !this.showFlagsConsistencia
								? medicao.insights.length && !medicao.validacaoConsistencia.length
								: medicao,
						)
						.filter((medicao) =>
							this.showFlagsConsistencia && !this.showFlagsComparativas
								? medicao.validacaoConsistencia.length && !medicao.insights.length
								: medicao,
						)
						.filter((medicao) =>
							this.showFlagsConsistencia && this.showFlagsComparativas
								? medicao.validacaoConsistencia.length && medicao.insights.length
								: medicao,
						)
						.filter((medicao) =>
							this.talhoesEscolhidos.length
								? this.talhoesEscolhidos.includes(medicao.talhao)
								: medicao,
						)
						.filter((medicao) =>
							this.parcelasEscolhidas.length
								? this.parcelasEscolhidas.includes(medicao.parcela)
								: medicao,
						)
						.map((medicao) => {
							return Object.assign(medicao, {
								x: index,
								color: !this.showCategoria
									? medicao.corCategoriaArvore
									: medicao.corCategoriaFitossanidade,
								y: Number(medicao[this.variavelEixoY]),
								marker: { symbol: this.symbol[medicao.nivelRisco] },
							})
						})
						.filter((value) => {
							return value.y != undefined
						}),
				}
			})
		},
		isQualitativo() {
			return [
				'tipoMatoDominante',
				'tipoMatoAssociado',
				'matoDominanteEntrelinha',
				'matoAssociadoEntrelinha',
				'categoriaFitossanidade',
				'categoriaDaArvore',
			].includes(this.variavelEixoX)
		},
		categories() {
			return this.dadosSeriesQualitativo.map((m) => m.name)
		},
		dadosSeries() {
			if (this.isQualitativo) {
				return this.dadosSeriesQualitativo
			}

			return this.dadosSeriesQuantitativo
		},
		dadosSeriesQuantitativo() {
			let series = []

			let keys = Object.keys(
				!this.showCategoria
					? this.medicoesAgrupadasPorCategoria
					: this.medicoesAgrupadasPorCategoriaFitossanidade,
			)
			keys = keys.filter((f) => !this.hideCategorias.includes(f))

			keys.forEach((key) => {
				const medicoes = _.cloneDeep(
					!this.showCategoria
						? this.medicoesAgrupadasPorCategoria[key]
						: this.medicoesAgrupadasPorCategoriaFitossanidade[key],
				)

				series.push({
					name: key,
					color: !this.showCategoria
						? this.corCategoriaArvore[key]
						: this.corCategoriaFitossanidade[key],
					opacity: 0.8,
					data: medicoes
						.filter(
							(medicao) =>
								!this.medicoesDesconsideradas.includes(medicao.id) && medicao.ht != undefined,
						)
						.filter((medicao) => !this.hideRisco.includes(medicao.nivelRisco))
						.filter((medicao) =>
							this.showFlagsComparativas && !this.showFlagsConsistencia
								? medicao.insights.length && !medicao.validacaoConsistencia.length
								: medicao,
						)
						.filter((medicao) =>
							this.showFlagsConsistencia && !this.showFlagsComparativas
								? medicao.validacaoConsistencia.length && !medicao.insights.length
								: medicao,
						)
						.filter((medicao) =>
							this.showFlagsConsistencia && this.showFlagsComparativas
								? medicao.validacaoConsistencia.length && medicao.insights.length
								: medicao,
						)
						.filter((medicao) =>
							this.talhoesEscolhidos.length
								? this.talhoesEscolhidos.includes(medicao.talhao)
								: medicao,
						)
						.filter((medicao) =>
							this.parcelasEscolhidas.length
								? this.parcelasEscolhidas.includes(medicao.parcela)
								: medicao,
						)
						.map((medicao) =>
							Object.assign(medicao, {
								x: Number(medicao[this.variavelEixoX]?.toString().replace(',', '.')),
								y: Number(medicao[this.variavelEixoY]?.toString().replace(',', '.')),
								marker: {
									symbol: this.symbol[medicao.nivelRisco],
								},
								selected: medicao.id === this.pointToEditId,
							}),
						),
					turboThreshold: Number.MAX_SAFE_INTEGER,
					boostThreshold: 2000,
					animation: false,
				})
			})

			return series
		},
		dadosSeriesDAP() {
			let series = []

			let keys = Object.keys(
				!this.showCategoria
					? this.medicoesAgrupadasPorCategoria
					: this.medicoesAgrupadasPorCategoriaFitossanidade,
			)
			keys = keys.filter((f) => !this.hideCategorias.includes(f))

			keys.forEach((key) => {
				const medicoes = _.cloneDeep(
					!this.showCategoria
						? this.medicoesAgrupadasPorCategoria[key]
						: this.medicoesAgrupadasPorCategoriaFitossanidade[key],
				)

				series.push({
					name: key,
					opacity: 0.8,
					color: !this.showCategoria
						? this.corCategoriaArvore[key]
						: this.corCategoriaFitossanidade[key],
					data: medicoes
						.filter(
						(medicao) => {
							// Filtra árvores desconsideradas
							if (this.medicoesDesconsideradas.includes(medicao.id)) return false
							// Filtra árvores sem DAP
							if (medicao.dap == undefined) return false
							// Se a variável do eixo X for DAP, permite árvores sem HT
							if (this.variavelEixoX === 'dap') return true
							// Para outras variáveis, requer HT
							return medicao.ht != undefined
						}
					)
						.filter((medicao) => !this.hideRisco.includes(medicao.nivelRisco))
						.filter((medicao) =>
							this.showFlagsComparativas && !this.showFlagsConsistencia
								? medicao.insights.length && !medicao.validacaoConsistencia.length
								: medicao,
						)
						.filter((medicao) =>
							this.showFlagsConsistencia && !this.showFlagsComparativas
								? medicao.validacaoConsistencia.length && !medicao.insights.length
								: medicao,
						)
						.filter((medicao) =>
							this.showFlagsConsistencia && this.showFlagsComparativas
								? medicao.validacaoConsistencia.length && medicao.insights.length
								: medicao,
						)
						.filter((medicao) =>
							this.talhoesEscolhidos.length
								? this.talhoesEscolhidos.includes(medicao.talhao)
								: medicao,
						)
						.filter((medicao) =>
							this.parcelasEscolhidas.length
								? this.parcelasEscolhidas.includes(medicao.parcela)
								: medicao,
						)
						.map((medicao) =>
							Object.assign(medicao, {
								x: 0,
								y: Number(medicao[this.variavelEixoX]?.toString().replace(',', '.')),
								marker: {
									symbol: this.symbol[medicao.nivelRisco],
								},
								selected: medicao.id === this.pointToEditId,
							}),
						),
					turboThreshold: Number.MAX_SAFE_INTEGER,
					boostThreshold: 2000,
					animation: false,
					type: 'scatter',
					point: {
						events: {
							click: (e) => {
								this.pointToEditId = e.point.id
								this.pointToEdit =
									this.medicoesNivelRisco.find((ponto) => ponto.id === e.point.id) || undefined
								this.mostrarCrudForm = true
							},
						},
					},
				})
			})

			series.unshift({
				type: 'boxplot',
				name: 'Observações',
				id: 'observ',
				color: 'rgb(255, 0, 0)',
				data: [
					[
						this.boxPlotDAPEstrato.LAI,
						this.boxPlotDAPEstrato.PRIMEIRO_QUARTIL,
						this.boxPlotDAPEstrato.MEDIANA,
						this.boxPlotDAPEstrato.TERCEIRO_QUARTIL,
						this.boxPlotDAPEstrato.LAS,
					],
				],
				tooltip: {
					formatter: function () {
						return 'some random string'
					},
				},
			})

			return series
		},
		dadosSeriesHT() {
			let series = []

			let keys = Object.keys(
				!this.showCategoria
					? this.medicoesAgrupadasPorCategoria
					: this.medicoesAgrupadasPorCategoriaFitossanidade,
			)
			keys = keys.filter((f) => !this.hideCategorias.includes(f))

			keys.forEach((key) => {
				const medicoes = _.cloneDeep(
					!this.showCategoria
						? this.medicoesAgrupadasPorCategoria[key]
						: this.medicoesAgrupadasPorCategoriaFitossanidade[key],
				)

				series.push({
					name: key,
					opacity: 0.8,
					color: !this.showCategoria
						? this.corCategoriaArvore[key]
						: this.corCategoriaFitossanidade[key],
					data: medicoes
						.filter(
						(medicao) => {
							// Filtra árvores desconsideradas
							if (this.medicoesDesconsideradas.includes(medicao.id)) return false
							// Se a variável do eixo Y for DAP, permite árvores sem HT
							if (this.variavelEixoY === 'dap') return true
							// Para outras variáveis, requer HT
							return medicao.ht != undefined
						}
					)
						.filter((medicao) => !this.hideRisco.includes(medicao.nivelRisco))
						.filter((medicao) =>
							this.showFlagsComparativas && !this.showFlagsConsistencia
								? medicao.insights.length && !medicao.validacaoConsistencia.length
								: medicao,
						)
						.filter((medicao) =>
							this.showFlagsConsistencia && !this.showFlagsComparativas
								? medicao.validacaoConsistencia.length && !medicao.insights.length
								: medicao,
						)
						.filter((medicao) =>
							this.showFlagsConsistencia && this.showFlagsComparativas
								? medicao.validacaoConsistencia.length && medicao.insights.length
								: medicao,
						)
						.filter((medicao) =>
							this.talhoesEscolhidos.length
								? this.talhoesEscolhidos.includes(medicao.talhao)
								: medicao,
						)
						.filter((medicao) =>
							this.parcelasEscolhidas.length
								? this.parcelasEscolhidas.includes(medicao.parcela)
								: medicao,
						)
						.map((medicao) =>
							Object.assign(medicao, {
								x: 0,
								y: medicao[this.variavelEixoY] ? Number(medicao[this.variavelEixoY]?.toString().replace(',', '.')) : 0,
								marker: {
									symbol: this.symbol[medicao.nivelRisco],
								},
								selected: medicao.id === this.pointToEditId,
								opacity: 0.4,
							}),
						),
					turboThreshold: Number.MAX_SAFE_INTEGER,
					boostThreshold: 2000,
					animation: false,
					type: 'scatter',
					point: {
						events: {
							click: (e) => {
								this.pointToEditId = e.point.id
								this.pointToEdit =
									this.medicoesNivelRisco.find((ponto) => ponto.id === e.point.id) || undefined
								this.mostrarCrudForm = true
							},
						},
					},
				})
			})

			series.unshift({
				type: 'boxplot',
				name: 'Observações',
				id: 'observ',
				color: 'rgb(255, 0, 0)',
				data: [
					[
						this.boxPlotAlturaEstrato.LAI,
						this.boxPlotAlturaEstrato.PRIMEIRO_QUARTIL,
						this.boxPlotAlturaEstrato.MEDIANA,
						this.boxPlotAlturaEstrato.TERCEIRO_QUARTIL,
						this.boxPlotAlturaEstrato.LAS,
					],
				],
				tooltip: {
					headerFormat: '<b>{point.series.name}</b><br/><br/>',
					pointFormat:
						'<b>- Mínimo: </b> {point.low} cm<br/>' +
						'<b>- Quartil 1: </b> {point.q1} cm<br/>' +
						'<b>- Mediana: </b> {point.median} cm<br/>' +
						'<b>- Quartil 3: </b> {point.q3} cm<br/>' +
						'<b>- Máximo: </b> {point.high} cm<br/>',
				},
			})

			return series
		},

		eixoXMinimo() {
			if (this.eixoXMaximoComZoom) return this.eixoXMinimoComZoom
			// Escala sempre começa em 0
			return 0
		},

		eixoXMaximo() {
			if (this.eixoXMaximoComZoom) return this.eixoXMaximoComZoom
			const maxDados = Math.max(
				...this.medicoesNivelRisco
					.filter((serie) => serie[this.variavelEixoX])
					.map((serie) => serie[this.variavelEixoX]),
			)
			// Inclui o LAS do boxplot para garantir que outliers acima sejam visíveis
			const maxBoxplot = this.boxPlotDAPEstrato.LAS
			return Math.max(maxDados, maxBoxplot)
		},

		eixoYMinimo() {
			if (this.eixoYMinimoComZoom) return this.eixoYMinimoComZoom
			// Escala sempre começa em 0
			return 0
		},

		eixoYMaximo() {
			if (this.eixoYMaximoComZoom) return this.eixoYMaximoComZoom
			const valores = this.medicoesNivelRisco
				.filter((serie) => serie[this.variavelEixoY] != null && serie[this.variavelEixoY] != undefined)
				.map((serie) => serie[this.variavelEixoY])
			const maxDados = valores.length > 0 ? Math.max(...valores) : 0
			// Inclui o LAS do boxplot para garantir que outliers acima sejam visíveis
			const maxBoxplot = this.boxPlotAlturaEstrato.LAS
			return Math.max(maxDados, maxBoxplot)
		},

		boxPlotDAPEstrato() {
			let daps = []

			// Se a variável do eixo X for CAP, converte para DAP dividindo por PI
			if (this.variavelEixoX === 'cap') {
				daps = this.series
					.filter((medicao) => medicao[this.variavelEixoX])
					.map((medicao) =>
						Number(
							(Number(medicao[this.variavelEixoX].toString().replace(',', '.')) / Math.PI).toFixed(
								'2',
							),
						),
					)
					.sort((a, b) => a - b)
			} else {
				// Para qualquer outra variável (DAP, HT, etc), usa o valor diretamente
				daps = this.series
					.filter((medicao) => medicao[this.variavelEixoX])
					.map((medicao) => Number(medicao[this.variavelEixoX].toString().replace(',', '.')))
					.sort((a, b) => a - b)
			}

			// Posicao dos quartis
			const QUANTIDADE_DE_DAPS = daps.length
			const POSICAO_DA_MEDIANA = Math.floor(QUANTIDADE_DE_DAPS / 2)
			const POSICAO_PRIMEIRO_QUARTIL = Math.floor(POSICAO_DA_MEDIANA / 2)

			// Encontrando os limites
			const MEDIANA = daps[POSICAO_DA_MEDIANA]
			const PRIMEIRO_QUARTIL = daps[POSICAO_PRIMEIRO_QUARTIL]
			const TERCEIRO_QUARTIL = daps[POSICAO_DA_MEDIANA + POSICAO_PRIMEIRO_QUARTIL]

			// 2 é a sensibilidade do boxplot, atualmente não oferecemos a possibilidade de alterar, portanto, fica fixo
			const LAS = TERCEIRO_QUARTIL + 2 * (TERCEIRO_QUARTIL - PRIMEIRO_QUARTIL)
			const LAI = PRIMEIRO_QUARTIL - 2 * (TERCEIRO_QUARTIL - PRIMEIRO_QUARTIL)

			return { PRIMEIRO_QUARTIL, LAI, MEDIANA, TERCEIRO_QUARTIL, LAS }
		},

		boxPlotAlturaEstrato() {
			const alturas = this.series
				.filter((medicao) => medicao[this.variavelEixoY] != null && medicao[this.variavelEixoY] != undefined)
				.map((medicao) => Number(medicao[this.variavelEixoY].toString().replace(',', '.')))
				.sort((a, b) => a - b)

			// Se não houver alturas, retorna valores padrão
			if (alturas.length === 0) {
				return { PRIMEIRO_QUARTIL: 0, LAI: 0, MEDIANA: 0, TERCEIRO_QUARTIL: 0, LAS: 0 }
			}

			// Posicao dos quartis
			const QUANTIDADE_DE_ALTURAS = alturas.length
			const POSICAO_DA_MEDIANA = Math.floor(QUANTIDADE_DE_ALTURAS / 2)
			const POSICAO_PRIMEIRO_QUARTIL = Math.floor(POSICAO_DA_MEDIANA / 2)

			// Encontrando os limites
			const MEDIANA = alturas[POSICAO_DA_MEDIANA]
			const PRIMEIRO_QUARTIL = alturas[POSICAO_PRIMEIRO_QUARTIL]
			const TERCEIRO_QUARTIL = alturas[POSICAO_DA_MEDIANA + POSICAO_PRIMEIRO_QUARTIL]

			// 2 é a sensibilidade do boxplot, atualmente não oferecemos a possibilidade de alterar, portanto, fica fixo
			const LAS = TERCEIRO_QUARTIL + 2 * (TERCEIRO_QUARTIL - PRIMEIRO_QUARTIL)
			const LAI = PRIMEIRO_QUARTIL - 2 * (TERCEIRO_QUARTIL - PRIMEIRO_QUARTIL)

			return { PRIMEIRO_QUARTIL, LAI, MEDIANA, TERCEIRO_QUARTIL, LAS }
		},
	},
	watch: {
		selectOnZoom(e) {
			if (!e)
				this.$refs.ref__chart.chart.eventOptions.selection = function () {
					return true
				}
		},
	},

	methods: {
		quantidadePorCategoria(categoria) {
			return !this.showCategoria
				? this.medicoesAgrupadasPorCategoria[categoria]?.filter(
						(f) => !this.medicoesDesconsideradas.includes(f.id),
					).length
				: this.medicoesAgrupadasPorCategoriaFitossanidade[categoria]?.filter(
						(f) => !this.medicoesDesconsideradas.includes(f.id),
					).length
		},
		limitesHTxIdade(idade) {
			//limite de aviso superior
			const LAS = Math.exp(3.2202694 + 0.2460725 * Math.log(idade) - (0.6868766 * 1) / idade)
			//limite de aviso inferior
			const LAI = Math.exp(2.2332588 + 0.3121552 * Math.log(idade) - (0.6089776 * 1) / idade)
			//limite de erro superior
			const LES = LAS + LAS * 0.15
			//limite de erro inferior
			const LEI = LAI + LAI * 0.15

			return {
				LAS,
				LAI,
				LES,
				LEI,
			}
		},

		limitesDAPHTxIdade(idade) {
			//limite de aviso superior
			const LAS =
				1.4459113233 +
				-0.3613213214 * idade +
				0.0698935354 * Math.pow(idade, 2) +
				-0.0046931676 * Math.pow(idade, 3) +
				0.0001022883 * Math.pow(idade, 4)
			//limite de aviso inferior
			const LAI =
				7.442517e-1 +
				-1.403595e-1 * idade +
				2.243259e-2 * Math.pow(idade, 2) +
				-1.358378e-3 * Math.pow(idade, 3) +
				2.856334e-5 * Math.pow(idade, 4)
			//limite de erro superior
			const LES = LAS + LAS * 0.15
			//limite de erro inferior
			const LEI = LAI + LAI * 0.15

			return {
				LAS,
				LAI,
				LES,
				LEI,
			}
		},
		desconsiderarMedicao() {
			this.medicoesDesconsideradas.push(this.pointToEdit.id)
			this.pointToEditId = undefined
			this.mostrarCrudForm = false
			this.$emit('desconsiderar', this.medicoesDesconsideradas)
		},
		async emitEditedPoint() {
			this.editing = true

			if (
				this.pointToEdit.dap &&
				typeof this.pointToEdit.dap === 'string' &&
				this.pointToEdit.dap.includes(',')
			) {
				this.pointToEdit.dap = this.pointToEdit.dap.replace(',', '.')
			}

			if (
				this.pointToEdit.ht &&
				typeof this.pointToEdit.ht === 'string' &&
				this.pointToEdit.ht.includes(',')
			) {
				this.pointToEdit.ht = this.pointToEdit.ht.replace(',', '.')
			}

			this.$emit('changePoint', this.pointToEdit)
			this.pointToEdit = undefined
			this.editing = false
			this.mostrarCrudForm = false
		},
		changeDap(e) {
			this.pointToEdit.dap = e
			this.emitEditedPoint()
		},
		changeHt(e) {
			this.pointToEdit.ht = e
			this.emitEditedPoint()
		},
		changeCategoria(e) {
			this.pointToEdit.categoriaDaArvore = e
			this.emitEditedPoint()
		},
		toggleCategoria(categoria) {
			if (this.hideCategorias.includes(categoria)) {
				// Remove a categoria da lista de ocultas
				this.hideCategorias = this.hideCategorias.filter((f) => f !== categoria)
			} else {
				// Adiciona a categoria à lista de ocultas usando spread para garantir reatividade
				this.hideCategorias = [...this.hideCategorias, categoria]
			}
		},
		toggleRisco(risco) {
			if (this.hideRisco.includes(risco)) {
				this.hideRisco = this.hideRisco.filter((f) => f !== risco)
			} else {
				this.hideRisco = [...this.hideRisco, risco]
			}
		},

		checkComma(event) {
			if (event.key === ',') {
				event.preventDefault()
				alert(
					' Atenção!, o campo não pode conter vírgula. Utilize o ponto para separar as casas decimais.',
				)
			}
		},

		exportarOutliersMedicao() {
			const temDadosQualidade = !this.medicoesAgrupadasPorCategoriaFitossanidade['null']?.length > 0

			const colunasInventarioFlorestal = [
				'talhao',
				'plantio',
				'parcela',
				'area_da_parcela (m²)',
				'data_do_plantio',
				'data_de_coleta',
				'linha',
				'arvore',
				'fuste',
				'cap (cm)',
				'dap (cm)',
				'ht (m)',
				'categoria_da_arvore',
				'inclinacao',
				'coordenada_x',
				'coordenada_y',
				'observacao',
				'nivel_de_risco',
			]
			const colunasInventarioQualidade = [
				'diametro_da_copa (cm)',
				'categoria_fitossanidade',
				'intensidade_categoria_fitossanidade (%)',
				'mato_dominante_linha',
				'mato_associado_linha',
				'altura_mato_linha (%)',
				'cobertura_linha (%)',
				'mato_dominante_entrelinha',
				'mato_associado_entrelinha',
				'altura_mato_entrelinha (%)',
				'cobertura_entrelinha (%)',
			]
			const colunasOutliers = [
				'outlier_1',
				'outlier_2',
				'outlier_3',
				'outlier_4',
				'outlier_5',
				'outlier_6',
				'outlier_7',
				'outlier_8',
				'outlier_9',
				'inconsistencia_1',
				'inconsistencia_2',
				'inconsistencia_3',
				'inconsistencia_4',
			]

			const colunas = colunasInventarioFlorestal.concat(
				temDadosQualidade ? colunasInventarioQualidade : [],
				colunasOutliers,
			)

			const dadosInventarioFlorestal = this.medicoesNivelRisco.map((medicao) => {
				return [
					medicao.talhao || medicao[this.bindColunas.talhao],
					medicao.plantio || medicao[this.bindColunas.plantio],
					medicao.parcela || medicao[this.bindColunas.parcela],
					medicao.areaParcela || medicao[this.bindColunas.areaParc],
					this.converterParaPadraoDeDataCorreto(medicao.dataPlantio) ||
						this.converterParaPadraoDeDataCorreto(medicao[this.bindColunas.dataPlantio]),
					this.converterParaPadraoDeDataCorreto(medicao.dataColeta) ||
						this.converterParaPadraoDeDataCorreto(medicao[this.bindColunas.dataColeta]),
					medicao.linha || medicao[this.bindColunas.linha],
					medicao.arvore || medicao[this.bindColunas.arvore],
					medicao.fuste || medicao[this.bindColunas.fuste],
					(medicao.cap || medicao[this.bindColunas.cap])?.toFixed(2).replace('.', ','),
					(medicao.dap || medicao[this.bindColunas.dap])?.toFixed(2).replace('.', ','),
					(medicao.ht || medicao[this.bindColunas.ht])?.toFixed(2).replace('.', ','),
					medicao.categoriaDaArvore || medicao[this.bindColunas.categoriaDaArvore],
					medicao.inclinacao || medicao[this.bindColunas.inclinacao],
					(medicao.coordenadaX ?? medicao[this.bindColunas.coordenadaX] ?? '')
						.toString()
						.replace(/\./g, ','),
					(medicao.coordenadaY ?? medicao[this.bindColunas.coordenadaY] ?? '')
						.toString()
						.replace(/\./g, ','),

					medicao.observacao || medicao[this.bindColunas.observacao],
					medicao.nivelRisco,
				]
			})

			const dadosOutliers = this.medicoesNivelRisco.map((medicao) => {
				if (medicao[this.bindColunas.ht]) {
					this.validaLimitesHTxIdade(medicao)
					this.validaLimitesDAPHTxIdade(medicao)
				}
				this.validaContextoLimitesContexto(medicao)
				this.setRiscoMedicao(medicao)

				const alturaIncompativel = medicao.validacaoConsistencia.find(
					(validacao) => validacao.tipo === 'CRESCIMENTO_ALTURA_INCOMPATIVEL',
				)
				const capIncompativel = medicao.validacaoConsistencia.find(
					(validacao) => validacao.tipo === 'CRESCIMENTO_CAP_INCOMPATIVEL',
				)
				const dapIncompativel = medicao.validacaoConsistencia.find(
					(validacao) => validacao.tipo === 'CRESCIMENTO_DAP_INCOMPATIVEL',
				)
				const categoriaIncompativel = medicao.validacaoConsistencia.find(
					(validacao) => validacao.tipo === 'CATEGORIA_INCOMPATIVEL',
				)

				return [
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.DAP_ABAIXO_PLANTIO) ||
							insight.message.includes(outliersMessage.DAP_ACIMA_PLANTIO),
					)?.message,
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.HT_ABAIXO_PLANTIO) ||
							insight.message.includes(outliersMessage.HT_ACIMA_PLANTIO),
					)?.message,
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.DAP_HT_ACIMA_PLANTIO) ||
							insight.message.includes(outliersMessage.DAP_HT_ACIMA_PLANTIO),
					)?.message,
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.DAP_ABAIXO_PLANTIO_IDADE) ||
							insight.message.includes(outliersMessage.DAP_ACIMA_PLANTIO_IDADE),
					)?.message,
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.HT_ABAIXO_PLANTIO_IDADE) ||
							insight.message.includes(outliersMessage.HT_ACIMA_PLANTIO_IDADE),
					)?.message,
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.DAP_HT_ABAIXO_PLANTIO_IDADE) ||
							insight.message.includes(outliersMessage.DAP_HT_ACIMA_PLANTIO_IDADE),
					)?.message,
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.DAP_ABAIXO_ESTRATO) ||
							insight.message.includes(outliersMessage.DAP_ACIMA_ESTRATO),
					)?.message,
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.HT_ABAIXO_ESTRATO) ||
							insight.message.includes(outliersMessage.HT_ACIMA_ESTRATO),
					)?.message,
					medicao.insights.find(
						(insight) =>
							insight.message.includes(outliersMessage.DAP_HT_ABAIXO_ESTRATO) ||
							insight.message.includes(outliersMessage.DAP_HT_ACIMA_ESTRATO),
					)?.message,
					alturaIncompativel
						? ValidacaoConsistenciaMessagesFactory.make(alturaIncompativel).long.message
						: undefined,
					dapIncompativel
						? ValidacaoConsistenciaMessagesFactory.make(dapIncompativel).long.message
						: undefined,
					capIncompativel
						? ValidacaoConsistenciaMessagesFactory.make(capIncompativel).long.message
						: undefined,
					categoriaIncompativel
						? ValidacaoConsistenciaMessagesFactory.make(categoriaIncompativel).long.message
						: undefined,
				]
			})

			const dadosQualidade = this.medicoesNivelRisco.map((medicao) => {
				return [
					medicao.diametroCopa || medicao[this.bindColunas.diametroCopa],
					medicao.categoriaFitossanidade || medicao[this.bindColunas.categoriaFitossanidade],
					medicao.intensidadeFitossanidade || medicao[this.bindColunas.intensidadeFitossanidade],
					medicao.tipoMatoDominante || medicao[this.bindColunas.matoDominanteLinha],
					medicao.tipoMatoAssociado || medicao[this.bindColunas.matoAssociadoLinha],
					medicao.alturaMatoLinha || medicao[this.bindColunas.alturaMatoLinha],
					medicao.coberturaMatoLinha || medicao[this.bindColunas.coberturaMatoLinha],
					medicao.matoDominanteEntrelinha || medicao[this.bindColunas.matoDominanteEntrelinha],
					medicao.matoAssociadoEntrelinha || medicao[this.bindColunas.matoAssociadoEntrelinha],
					medicao.alturaMatoEntrelinha || medicao[this.bindColunas.alturaMatoEntrelinha],
					medicao.coberturaMatoEntrelinha || medicao[this.bindColunas.coberturaMatoEntrelinha],
				]
			})

			console.log('csv', [
				colunas,
				...dadosInventarioFlorestal.concat(
					temDadosQualidade ? colunasInventarioQualidade : [],
					dadosOutliers,
				),
			])
			const csv = [
				colunas,
				...dadosInventarioFlorestal.map((dado, index) => [
					...dado,
					...(temDadosQualidade ? dadosQualidade[index] : []),
					...dadosOutliers[index],
				]),
			]
				.filter((f) => f?.length)
				.map((registro) => registro.join(';'))
				.join('\r\n')

			download(csv, 'Análise de Outliers.csv', 'text/csv')
		},

		/**
		 *  Converte data para o padrao correto do sistema: dd/MM/yyyy
		 */
		converterParaPadraoDeDataCorreto(dataFormatoOriginal) {
			if (this.dataEstaNoPadraoISO(dataFormatoOriginal)) {
				var dataFormatoCorreto = dataFormatoOriginal.split('T')[0]
				var dataSeparada = dataFormatoCorreto.split('-')
				dataFormatoCorreto = `${dataSeparada[2]}/${dataSeparada[1]}/${dataSeparada[0]}`
				return dataFormatoCorreto
			}
			return dataFormatoOriginal
		},

		/**
		 * Verifica se data esta no padrao ISO 8601
		 */
		dataEstaNoPadraoISO(data) {
			const EXPRESSAO_PARA_ISO_8601 = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/
			return EXPRESSAO_PARA_ISO_8601.test(data)
		},
	},
}
</script>

<style>
.container_grid__outliers {
	display: grid;
	width: 85%;
	grid-template:
		'boxplot_dap boxplot_espaco'
		'boxplot_dap_ht  boxplot_ht';
	grid-template-columns: 7fr 1fr;
}

.container_grid__outliers_boxplot_dap {
	height: 10rem;
	grid-area: boxplot_dap;
}

.container_grid__outliers_boxplot_space {
	grid-area: boxplot_espaco;
}

.container_grid__outliers_boxplot_dap_ht {
	grid-area: boxplot_dap_ht;
}

.container_grid__outliers_boxplot_ht {
	grid-area: boxplot_ht;
}

/* @import url("../../css/tooltipSimplesHighcharts.css"); */
.container__grafico__dispersao {
	display: block;
	flex-direction: column;
	height: 100%;
}

.highcharts-container {
	margin: 0 !important;
	height: 100% !important;
}

.textoinformativo {
	color: rgb(149, 187, 73);
	text-align: center;
	vertical-align: bottom;
	font-weight: 800;
}

.tamanhoButton {
	width: 210px;
}

.divTexto {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
}

#dispersao__parcelas {
	height: 100%;
	max-height: 90%;
}
</style>
